import React from 'react';
import type {
  InnerLegacyButtonProps,
  LegacyButtonIconConfig,
  LegacyButtonBadgeConfig,
  LegacyButtonSlotConfig,
  LegacyVisualSlotType,
} from '@/shared/types/legacyButton';
import type { LegacyIconName } from '@/shared/types/legacyIcon';
import { ButtonTooltipConfig } from '@/shared/types/buttonConfig';
import { warnDeprecatedPropList } from '@/modules/InnerButton/utils/deprecation';
import { isLikelyUrl } from './legacySlotUrl';

export const convertLegacyIconToLegacyIconConfig = (
  props: Pick<
    InnerLegacyButtonProps,
    | 'icon'
    | 'iconDuotonePrimary'
    | 'iconDuotoneSecondary'
    | 'iconDuotoneOpacityPrimary'
    | 'iconDuotoneOpacitySecondary'
    | 'colorIcon'
    | 'classNameIcon'
    | 'strokeWidth'
    | 'onClickIcon'
  >,
): LegacyButtonIconConfig | undefined => {
  if (!props.icon) return undefined;

  if (React.isValidElement(props.icon)) {
    return undefined;
  }

  if (typeof props.icon === 'string' && isLikelyUrl(props.icon)) {
    return undefined;
  }

  const hasStylingProps =
    props.iconDuotonePrimary ||
    props.iconDuotoneSecondary ||
    props.iconDuotoneOpacityPrimary !== undefined ||
    props.iconDuotoneOpacitySecondary !== undefined ||
    props.colorIcon ||
    props.classNameIcon ||
    props.strokeWidth !== undefined ||
    props.onClickIcon;

  if (!hasStylingProps) {
    return undefined;
  }

  const deprecatedUsed: string[] = [];
  if (props.iconDuotonePrimary) deprecatedUsed.push('iconDuotonePrimary');
  if (props.iconDuotoneSecondary) deprecatedUsed.push('iconDuotoneSecondary');
  if (props.iconDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('iconDuotoneOpacityPrimary');
  }
  if (props.iconDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('iconDuotoneOpacitySecondary');
  }
  if (props.colorIcon) deprecatedUsed.push('colorIcon');
  if (props.classNameIcon) deprecatedUsed.push('classNameIcon');
  if (props.strokeWidth !== undefined) deprecatedUsed.push('strokeWidth');
  if (props.onClickIcon) deprecatedUsed.push('onClickIcon');
  warnDeprecatedPropList(deprecatedUsed, 'iconProps');

  return {
    icon: props.icon as LegacyIconName,
    show: true,
    color: props.colorIcon,
    className: props.classNameIcon,
    onClick: props.onClickIcon,
    colorDuotonePrimary: props.iconDuotonePrimary,
    colorDuotoneSecondary: props.iconDuotoneSecondary,
    opacityDuotonePrimary: props.iconDuotoneOpacityPrimary,
    opacityDuotoneSecondary: props.iconDuotoneOpacitySecondary,
    iconDuotonePrimary: props.iconDuotonePrimary,
    iconDuotoneSecondary: props.iconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.iconDuotoneOpacitySecondary,
  };
};

export const convertLegacyBadgeToLegacyBadgeConfig = (
  props: Pick<
    InnerLegacyButtonProps,
    | 'badgeColor'
    | 'badgeLabel'
    | 'badgeTheme'
    | 'badgeSize'
    | 'badgeStroke'
    | 'badgeIcon'
    | 'badgeIconDuotonePrimary'
    | 'badgeIconDuotoneSecondary'
    | 'badgeIconDuotoneOpacityPrimary'
    | 'badgeIconDuotoneOpacitySecondary'
    | 'badgeLoading'
  >,
): LegacyButtonBadgeConfig | undefined => {
  if (!props.badgeColor && !props.badgeLabel && !props.badgeIcon && !props.badgeLoading) {
    return undefined;
  }

  const deprecatedUsed: string[] = [];
  if (props.badgeColor) deprecatedUsed.push('badgeColor');
  if (props.badgeLabel) deprecatedUsed.push('badgeLabel');
  if (props.badgeIcon) deprecatedUsed.push('badgeIcon');
  if (props.badgeLoading) deprecatedUsed.push('badgeLoading');
  warnDeprecatedPropList(deprecatedUsed, 'badge');

  return {
    label: props.badgeLabel,
    color: props.badgeColor,
    theme: props.badgeTheme,
    size: props.badgeSize,
    stroke: props.badgeStroke,
    icon: props.badgeIcon,
    iconDuotonePrimary: props.badgeIconDuotonePrimary,
    iconDuotoneSecondary: props.badgeIconDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.badgeIconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.badgeIconDuotoneOpacitySecondary,
    loading: props.badgeLoading,
    show: true,
  };
};

export const convertLegacyLeftSlotToLegacySlotConfig = (
  props: Pick<
    InnerLegacyButtonProps,
    | 'leftSlot'
    | 'leftSlotDuotonePrimary'
    | 'leftSlotDuotoneSecondary'
    | 'leftSlotDuotoneOpacityPrimary'
    | 'leftSlotDuotoneOpacitySecondary'
    | 'onClickLeftSlot'
    | 'colorIcon'
  >,
): LegacyButtonSlotConfig | undefined => {
  if (!props.leftSlot) return undefined;

  const hasStylingProps =
    props.leftSlotDuotonePrimary ||
    props.leftSlotDuotoneSecondary ||
    props.leftSlotDuotoneOpacityPrimary !== undefined ||
    props.leftSlotDuotoneOpacitySecondary !== undefined ||
    props.onClickLeftSlot ||
    props.colorIcon;

  if (!hasStylingProps) {
    return undefined;
  }

  const deprecatedUsed: string[] = [];
  if (props.leftSlotDuotonePrimary) deprecatedUsed.push('leftSlotDuotonePrimary');
  if (props.leftSlotDuotoneSecondary) deprecatedUsed.push('leftSlotDuotoneSecondary');
  if (props.leftSlotDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('leftSlotDuotoneOpacityPrimary');
  }
  if (props.leftSlotDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('leftSlotDuotoneOpacitySecondary');
  }
  if (props.onClickLeftSlot) deprecatedUsed.push('onClickLeftSlot');
  if (props.colorIcon) deprecatedUsed.push('colorIcon');
  warnDeprecatedPropList(deprecatedUsed, 'leftSlotProps');

  return {
    icon: props.leftSlot as LegacyVisualSlotType,
    colorIcon: props.colorIcon,
    onClick: props.onClickLeftSlot,
    iconDuotonePrimary: props.leftSlotDuotonePrimary,
    iconDuotoneSecondary: props.leftSlotDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.leftSlotDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.leftSlotDuotoneOpacitySecondary,
    show: true,
  };
};

export const convertLegacyRightSlotToLegacySlotConfig = (
  props: Pick<
    InnerLegacyButtonProps,
    | 'rightSlot'
    | 'rightSlotDuotonePrimary'
    | 'rightSlotDuotoneSecondary'
    | 'rightSlotDuotoneOpacityPrimary'
    | 'rightSlotDuotoneOpacitySecondary'
    | 'onClickRightSlot'
    | 'colorIcon'
  >,
): LegacyButtonSlotConfig | undefined => {
  if (!props.rightSlot) return undefined;

  const hasStylingProps =
    props.rightSlotDuotonePrimary ||
    props.rightSlotDuotoneSecondary ||
    props.rightSlotDuotoneOpacityPrimary !== undefined ||
    props.rightSlotDuotoneOpacitySecondary !== undefined ||
    props.onClickRightSlot ||
    props.colorIcon;

  if (!hasStylingProps) {
    return undefined;
  }

  const deprecatedUsed: string[] = [];
  if (props.rightSlotDuotonePrimary) deprecatedUsed.push('rightSlotDuotonePrimary');
  if (props.rightSlotDuotoneSecondary) deprecatedUsed.push('rightSlotDuotoneSecondary');
  if (props.rightSlotDuotoneOpacityPrimary !== undefined) {
    deprecatedUsed.push('rightSlotDuotoneOpacityPrimary');
  }
  if (props.rightSlotDuotoneOpacitySecondary !== undefined) {
    deprecatedUsed.push('rightSlotDuotoneOpacitySecondary');
  }
  if (props.onClickRightSlot) deprecatedUsed.push('onClickRightSlot');
  if (props.colorIcon) deprecatedUsed.push('colorIcon');
  warnDeprecatedPropList(deprecatedUsed, 'rightSlotProps');

  return {
    icon: props.rightSlot as LegacyVisualSlotType,
    colorIcon: props.colorIcon,
    onClick: props.onClickRightSlot,
    iconDuotonePrimary: props.rightSlotDuotonePrimary,
    iconDuotoneSecondary: props.rightSlotDuotoneSecondary,
    iconDuotoneOpacityPrimary: props.rightSlotDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary: props.rightSlotDuotoneOpacitySecondary,
    show: true,
  };
};

export const convertLegacyTooltipToTooltipConfig = (
  props: Pick<
    InnerLegacyButtonProps,
    | 'tooltip'
    | 'tooltipPosition'
    | 'tooltipAlignment'
    | 'tooltipDelay'
    | 'tooltipAbsolute'
    | 'classNameTooltip'
  >,
): ButtonTooltipConfig | undefined => {
  if (!props.tooltip || typeof props.tooltip !== 'string') return undefined;

  const hasStylingProps =
    props.tooltipPosition ||
    props.tooltipAlignment ||
    props.tooltipDelay !== undefined ||
    props.tooltipAbsolute !== undefined ||
    props.classNameTooltip;

  if (!hasStylingProps) {
    return undefined;
  }

  const deprecatedUsed: string[] = [];
  if (props.tooltipPosition) deprecatedUsed.push('tooltipPosition');
  if (props.tooltipAlignment) deprecatedUsed.push('tooltipAlignment');
  if (props.tooltipDelay !== undefined) deprecatedUsed.push('tooltipDelay');
  if (props.tooltipAbsolute !== undefined) deprecatedUsed.push('tooltipAbsolute');
  if (props.classNameTooltip) deprecatedUsed.push('classNameTooltip');
  warnDeprecatedPropList(deprecatedUsed, 'tooltip como objeto o tooltipProps');

  return {
    label: props.tooltip,
    position: props.tooltipPosition,
    alignment: props.tooltipAlignment,
    delay: props.tooltipDelay,
    absolute: props.tooltipAbsolute,
    className: props.classNameTooltip,
    show: true,
  };
};
